import {
    type CSSProperties,
    type ReactNode,
    useEffect,
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    Skeleton,
    Button,
    Avatar,
    Text
} from "ncore-ui-kit";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";

const NAME = "Skeleton";

const USAGE_CODE = `const [isLoading, setIsLoading] = useState(true);

<Skeleton.Group
    placeholder={<>
        <Skeleton
            variant="circle"
            size="medium"
        />
        <Skeleton
            typography="bodyMediumSize"
            variant="text"
            lines={2}
        />
    </>}
    loadingLabel="Loading profile"
    loadedLabel="Profile loaded"
    isLoading={isLoading}
>
    <Profile />
</Skeleton.Group>`;

const ANIMATIONS = [
    "none",
    "pulse",
    "shimmer"
] as const;

const TYPOGRAPHIES = [
    "titleLargeSize",
    "bodyLargeSize",
    "bodyMediumSize",
    "bodySmallSize"
] as const;

const AVATAR_SIZES = [
    "xxSmall",
    "xSmall",
    "small",
    "medium",
    "large",
    "xLarge"
] as const;

const LOAD_DELAY = 2000;

const PROFILE_STYLE: CSSProperties = {
    alignItems: "center",
    display: "flex",
    gap: 16
};

const LINES_STYLE: CSSProperties = {
    flexDirection: "column",
    display: "flex",
    flex: 1,
    gap: 4
};

const ITEM_STYLE: CSSProperties = {
    flexDirection: "column",
    alignItems: "center",
    display: "flex",
    gap: 8
};

const ITEMS_STYLE: CSSProperties = {
    justifyContent: "center",
    alignItems: "flex-end",
    flexWrap: "wrap",
    display: "flex",
    gap: 24
};

const ROW_STYLE: CSSProperties = {
    flexDirection: "column",
    display: "flex",
    gap: 8
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("section.variants"),
        id: "variants"
    },
    {
        title: t("skeletonPage.textTitle"),
        id: "text"
    },
    {
        title: t("section.sizes"),
        id: "sizes"
    },
    {
        title: t("skeletonPage.animationTitle"),
        id: "animation"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const SkeletonPage = () => {
    const {
        t
    } = useDocsI18n();

    const [
        isLoading,
        setIsLoading
    ] = useState(true);

    const [
        animation,
        setAnimation
    ] = useState<(typeof ANIMATIONS)[number]>("none");

    useEffect(() => {
        if(!isLoading) {
            return;
        }

        const timer = window.setTimeout(() => setIsLoading(false), LOAD_DELAY);

        return () => {
            window.clearTimeout(timer);
        };
    }, [
        isLoading
    ]);

    const handleReload = () => setIsLoading(true);

    const handleNextAnimation = () => {
        const nextIndex = (ANIMATIONS.indexOf(animation) + 1) % ANIMATIONS.length;

        setAnimation(ANIMATIONS[nextIndex] ?? "none");
    };

    const renderItem = (label: string, node: ReactNode) => <div
        style={ITEM_STYLE}
        key={label}
    >
        {node}
        <Text
            variant="bodySmallSize"
            color="low"
        >
            {label}
        </Text>
    </div>;

    const renderRow = (label: string, node: ReactNode) => <div
        style={ROW_STYLE}
        key={label}
    >
        {node}
        <Text
            variant="bodySmallSize"
            color="low"
        >
            {label}
        </Text>
    </div>;

    return <Article>
        <ComponentHeader
            lead={t("skeletonPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("skeletonPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("skeletonPage.overviewCaption")}
                isStacked={true}
            >
                <Skeleton.Group
                    loadingLabel={t("skeletonPage.loadingLabel")}
                    loadedLabel={t("skeletonPage.loadedLabel")}
                    placeholder={<div
                        style={PROFILE_STYLE}
                    >
                        <Skeleton
                            variant="circle"
                            size="medium"
                        />
                        <div
                            style={LINES_STYLE}
                        >
                            <Skeleton
                                typography="labelLargeSize"
                                variant="text"
                                width="40%"
                            />
                            <Skeleton
                                typography="bodyMediumSize"
                                variant="text"
                                width="80%"
                            />
                        </div>
                    </div>}
                    isLoading={isLoading}
                >
                    <div
                        style={PROFILE_STYLE}
                    >
                        <Avatar
                            title={t("sample.name")}
                            size="medium"
                        />
                        <div
                            style={LINES_STYLE}
                        >
                            <Text
                                variant="labelLargeSize"
                                color="high"
                            >
                                {t("sample.name")}
                            </Text>
                            <Text
                                variant="bodyMediumSize"
                                color="mid"
                            >
                                {t("skeletonPage.bio")}
                            </Text>
                        </div>
                    </div>
                </Skeleton.Group>
                <Button
                    title={t("skeletonPage.reload")}
                    isDisabled={isLoading}
                    onPress={handleReload}
                    variant="outline"
                    type="neutral"
                    size="small"
                />
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("section.variants")}
            id="variants"
        >
            <Paragraph>
                {t("skeletonPage.variantsText")}
            </Paragraph>
            <Preview>
                <div
                    style={ITEMS_STYLE}
                >
                    {
                        renderItem("variant=\"circle\"", <Skeleton
                            animation="none"
                            variant="circle"
                        />)
                    }
                    {
                        renderItem("variant=\"icon\"", <Skeleton
                            animation="none"
                            variant="icon"
                        />)
                    }
                    {
                        renderItem("variant=\"block\"", <Skeleton
                            animation="none"
                            variant="block"
                            width={120}
                        />)
                    }
                    {
                        renderItem("variant=\"text\"", <Skeleton
                            animation="none"
                            variant="text"
                            width={160}
                            lines={3}
                        />)
                    }
                </div>
            </Preview>
        </Section>
        <Section
            title={t("skeletonPage.textTitle")}
            id="text"
        >
            <Paragraph>
                {t("skeletonPage.textText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    TYPOGRAPHIES.map((typography) => renderRow(`typography="${typography}"`, <Skeleton
                        typography={typography}
                        animation="none"
                        variant="text"
                        width="70%"
                    />))
                }
                {
                    renderRow("lines={3}", <Skeleton
                        animation="none"
                        variant="text"
                        lines={3}
                    />)
                }
            </Preview>
        </Section>
        <Section
            title={t("section.sizes")}
            id="sizes"
        >
            <Paragraph>
                {t("skeletonPage.sizesText")}
            </Paragraph>
            <Preview>
                <div
                    style={ITEMS_STYLE}
                >
                    {
                        AVATAR_SIZES.map((size) => renderItem(size, <Skeleton
                            animation="none"
                            variant="circle"
                            size={size}
                        />))
                    }
                </div>
            </Preview>
            <Preview>
                <div
                    style={ITEMS_STYLE}
                >
                    {
                        renderItem("aspectRatio={16 / 9} radius=\"surface\"", <Skeleton
                            aspectRatio={16 / 9}
                            animation="none"
                            radius="surface"
                            width={240}
                        />)
                    }
                    {
                        renderItem("height={40} radius=\"actions\"", <Skeleton
                            animation="none"
                            radius="actions"
                            height={40}
                            width={120}
                        />)
                    }
                </div>
            </Preview>
        </Section>
        <Section
            title={t("skeletonPage.animationTitle")}
            id="animation"
        >
            <Paragraph>
                {t("skeletonPage.animationText")}
            </Paragraph>
            <Preview
                caption={t("skeletonPage.animationCaption")}
                isStacked={true}
            >
                <div
                    style={PROFILE_STYLE}
                >
                    <Skeleton
                        animation={animation}
                        variant="circle"
                        size="medium"
                    />
                    <div
                        style={LINES_STYLE}
                    >
                        <Skeleton
                            typography="labelLargeSize"
                            animation={animation}
                            variant="text"
                            width="40%"
                        />
                        <Skeleton
                            typography="bodyMediumSize"
                            animation={animation}
                            variant="text"
                            width="80%"
                        />
                    </div>
                </div>
                <Button
                    title={`animation="${animation}"`}
                    onPress={handleNextAnimation}
                    variant="outline"
                    type="neutral"
                    size="small"
                />
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("skeletonPage.a11yHidden"),
                    t("skeletonPage.a11yGroup"),
                    t("skeletonPage.a11yLive"),
                    t("skeletonPage.a11yMotion")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        type: "\"circle\" | \"block\" | \"text\" | \"icon\"",
                        description: t("skeletonPage.propVariant"),
                        defaultValue: "\"block\"",
                        name: "variant"
                    },
                    {
                        description: t("skeletonPage.propAnimation"),
                        type: "\"shimmer\" | \"pulse\" | \"none\"",
                        defaultValue: "\"pulse\"",
                        name: "animation"
                    },
                    {
                        description: t("skeletonPage.propLines"),
                        defaultValue: "1",
                        name: "lines",
                        type: "number"
                    },
                    {
                        description: t("skeletonPage.propTypography"),
                        defaultValue: "\"bodyMediumSize\"",
                        type: "keyof Typography",
                        name: "typography"
                    },
                    {
                        description: t("skeletonPage.propWidth"),
                        type: "DimensionValue",
                        name: "width"
                    },
                    {
                        description: t("skeletonPage.propHeight"),
                        name: "height",
                        type: "number"
                    },
                    {
                        description: t("skeletonPage.propAspectRatio"),
                        name: "aspectRatio",
                        type: "number"
                    },
                    {
                        type: "\"xxSmall\" | \"xSmall\" | \"small\" | \"medium\" | \"large\" | \"xLarge\" | number",
                        description: t("skeletonPage.propSize"),
                        name: "size"
                    },
                    {
                        type: "\"surface\" | \"actions\" | \"form\" | \"full\" | \"none\" | number",
                        description: t("skeletonPage.propRadius"),
                        name: "radius"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("skeletonPage.propCustomTheme"),
                        name: "customTheme"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("skeletonPage.propsNote")}
            </Paragraph>
            <Paragraph>
                {t("skeletonPage.groupText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("skeletonPage.propGroupIsLoading"),
                        name: "isLoading",
                        type: "boolean"
                    },
                    {
                        description: t("skeletonPage.propGroupPlaceholder"),
                        name: "placeholder",
                        type: "ReactNode"
                    },
                    {
                        description: t("skeletonPage.propGroupChildren"),
                        type: "ReactNode",
                        name: "children"
                    },
                    {
                        description: t("skeletonPage.propGroupLoadingLabel"),
                        name: "loadingLabel",
                        type: "string"
                    },
                    {
                        description: t("skeletonPage.propGroupLoadedLabel"),
                        name: "loadedLabel",
                        type: "string"
                    },
                    {
                        description: t("skeletonPage.propGroupCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default SkeletonPage;
